import { RawDataRowProvenance } from '../data/api';

/**
 * Compare two revisions of the same ingest file.
 *
 * The backend computes an equivalent breakdown at upload time and returns it as
 * `duplicate_of.row_diff`, but **it is not persisted** — `DataSession` has no
 * diff column, so there is nothing to read back later. Both revisions' rows do
 * survive (a re-upload leaves the superseded rows in place), so the comparison
 * is recomputed here from the rows themselves. That needs no migration and no
 * new endpoint; the cost is that it only covers revisions whose rows are still
 * present, which is all of them today.
 *
 * Direction is "what the newer revision did to the older one": `added` rows
 * appear only in the newer, `removed` only in the older.
 */
export interface RawRevisionDiff {
  added: number;
  removed: number;
  changed: number;
  unchanged: number;
  /** Field names whose value differs on at least one row, most-affected first. */
  changedFields: string[];
  /** Fields the newer revision introduced (absent from every older row). */
  addedFields: string[];
  /** Fields the newer revision dropped. */
  removedFields: string[];
}

/**
 * Identity of a row across revisions. The EID is the real key — row numbers
 * shift when a revision inserts or drops a line, so keying on them alone
 * reports a whole file as "changed" after a single insertion. Rows with no EID
 * fall back to their row number, which is the best available and is why a
 * file without identifiers can still show spurious changes.
 */
function rowKey(row: RawDataRowProvenance): string {
  const mappedEid = (row.mapped_data || {}).eid;
  const rawEid = (row.raw_data || {}).eid ?? (row.raw_data || {}).EID;
  const eid = mappedEid || rawEid;
  return eid ? `eid:${String(eid)}` : `row:${String(row.row_number ?? '')}`;
}

function fieldsOf(rows: RawDataRowProvenance[]): Set<string> {
  const out = new Set<string>();
  rows.forEach((r) => Object.keys(r.raw_data || {}).forEach((k) => out.add(k)));
  return out;
}

export function diffRawRevisions(
  older: RawDataRowProvenance[],
  newer: RawDataRowProvenance[],
): RawRevisionDiff {
  const olderByKey = new Map(older.map((r) => [rowKey(r), r]));
  const newerByKey = new Map(newer.map((r) => [rowKey(r), r]));

  let changed = 0;
  let unchanged = 0;
  const fieldHits = new Map<string, number>();

  newerByKey.forEach((newRow, key) => {
    const oldRow = olderByKey.get(key);
    if (!oldRow) return;
    const a = (oldRow.raw_data || {}) as Record<string, unknown>;
    const b = (newRow.raw_data || {}) as Record<string, unknown>;
    // Union of both sides: a field the newer revision added counts as a change
    // to that row, and so does one it dropped.
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    let rowChanged = false;
    keys.forEach((f) => {
      const av = a[f] == null ? '' : String(a[f]);
      const bv = b[f] == null ? '' : String(b[f]);
      if (av !== bv) {
        rowChanged = true;
        fieldHits.set(f, (fieldHits.get(f) || 0) + 1);
      }
    });
    if (rowChanged) changed += 1;
    else unchanged += 1;
  });

  let added = 0;
  newerByKey.forEach((_row, key) => { if (!olderByKey.has(key)) added += 1; });
  let removed = 0;
  olderByKey.forEach((_row, key) => { if (!newerByKey.has(key)) removed += 1; });

  const olderFields = fieldsOf(older);
  const newerFields = fieldsOf(newer);

  return {
    added,
    removed,
    changed,
    unchanged,
    changedFields: [...fieldHits.entries()]
      .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
      .map(([f]) => f),
    addedFields: [...newerFields].filter((f) => !olderFields.has(f)).sort(),
    removedFields: [...olderFields].filter((f) => !newerFields.has(f)).sort(),
  };
}

/** One-line summary for the revision list, e.g. "3 changed · 2 fields added". */
export function summariseRevisionDiff(d: RawRevisionDiff): string {
  const parts: string[] = [];
  if (d.added) parts.push(`${d.added} row${d.added === 1 ? '' : 's'} added`);
  if (d.removed) parts.push(`${d.removed} row${d.removed === 1 ? '' : 's'} removed`);
  if (d.changed) parts.push(`${d.changed} changed`);
  if (d.addedFields.length) parts.push(`${d.addedFields.length} field${d.addedFields.length === 1 ? '' : 's'} added`);
  if (d.removedFields.length) parts.push(`${d.removedFields.length} field${d.removedFields.length === 1 ? '' : 's'} removed`);
  if (parts.length === 0) return 'identical to the current revision';
  return parts.join(' · ');
}
